// crewbit-admin/src/components/money/SetupHstBanner.tsx
//
// Page-level amber banner above the Tax page hero. Visibility:
//   - hst_number missing OR tax_mode === 'exempt'
//
// Calm-empty discipline: hidden when an HST number is on file AND tax_mode
// is 'charge' or 'quick_method' — parent gates render.
//
// Click routes to /settings/tax (legacy admin Settings TaxCompliance page).
// When §Settings hub admin UI ships (Session 139 — Tier 1), this destination
// swaps to /money/settings/taxes.
//
// Spec §Tax.7.2 + §Tax.D1 (CTA).

import { Receipt, ChevronRight } from 'lucide-react';
import { palette, admin, radii, spacing, transition } from '../../styles/tokens';

interface SetupHstBannerProps {
  /** When true, copy reads as "Not registered for HST" (tax_mode === 'exempt'). */
  exempt?: boolean;
  onClick: () => void;
}

export function SetupHstBanner({ exempt = false, onClick }: SetupHstBannerProps) {
  const title = exempt ? 'Not registered for HST' : 'Add your HST number';
  const body = exempt
    ? "Your business is set to exempt. If that's wrong, register and update your settings to start tracking HST."
    : 'You need to register an HST number before you can file remittances or generate a CRA-ready summary.';
  const cta = exempt ? 'Update tax settings' : 'Add HST number';

  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: spacing.md,
        width: '100%',
        padding: `${spacing.md}px ${spacing.lg}px`,
        background: palette.amberWash,
        border: `1px solid ${palette.amber}`,
        borderRadius: admin.cardRadius,
        marginBottom: spacing.md,
        cursor: 'pointer',
        textAlign: 'left',
        fontFamily: 'inherit',
        transition: `background ${transition.fast}`,
      }}
      onMouseEnter={(e) => (e.currentTarget.style.background = palette.amberWash)}
      onMouseLeave={(e) => (e.currentTarget.style.background = palette.amberWash)}
    >
      <div
        style={{
          width: 40,
          height: 40,
          borderRadius: radii.sm,
          background: palette.white,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
        }}
      >
        <Receipt size={20} color={palette.amber} strokeWidth={2} />
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 14, fontWeight: 600, color: palette.amber }}>{title}</div>
        <div style={{ fontSize: 12.5, color: palette.amber, marginTop: 2, opacity: 0.85 }}>
          {body}
        </div>
      </div>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 4,
          padding: '6px 12px',
          borderRadius: radii.sm,
          background: palette.amber,
          color: palette.white,
          fontSize: 12.5,
          fontWeight: 600,
          flexShrink: 0,
        }}
      >
        {cta}
        <ChevronRight size={14} strokeWidth={2.5} />
      </div>
    </button>
  );
}
